import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, Card, Typography, TextField, Button, Avatar, IconButton, InputAdornment, 
  useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Stepper, Step, StepLabel
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import KeyIcon from '@mui/icons-material/Key';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import RegisterDrawer from './RegisterDrawer';
import { useTranslation } from 'react-i18next';
import { syncCurrencyAfterAuth } from './currencyHelpers';
import config from './config';

const Login = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState({ phone: '', password: '' });
  const [message, setMessage] = useState('');
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  const [showPassword, setShowPassword] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const navigate = useNavigate();
  const [registerOpen, setRegisterOpen] = useState(false);

  // ── FORGOT PASSWORD STATE ──
  const [forgotDialogOpen, setForgotDialogOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotPhone, setForgotPhone] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
  const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);
  
  const handleChange = e => {
    if (e.target.name === 'phone') {
      const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
      setForm({ ...form, phone: val });
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };
  
  const { loginAdmin } = require('./AdminPortal/adminsApi');
  
  const handleSubmit = async e => {
    e.preventDefault();
    
    try {
      const adminRes = await loginAdmin(`+967${form.phone}`, form.password);
      if (adminRes && adminRes.success) {
        // ✅ ADMIN PERMISSION CHECK: Admin must have Permission === true
        if (adminRes.user && adminRes.user.Permission !== true) {
          setMessage(t('login.permissionDenied', 'Access denied: Permission required for admin access.') || 'Access denied: Permission required for admin access.');
          return;
        }

        setMessage(adminRes.message || t('login.loginSuccess') || 'Login successful!');
        localStorage.setItem('admin_token', adminRes.token || '');
        localStorage.setItem('admin_user', JSON.stringify(adminRes.user));
        window.dispatchEvent(new Event('adminUserChanged'));
        
        if (adminRes.user && (adminRes.user.Id || adminRes.user.id)) {
          try {
            await syncCurrencyAfterAuth(adminRes.user.Id || adminRes.user.id);
          } catch (error) {
            console.error('⚠️ Could not sync admin currency after login:', error);
          }
        }
        
        window.location.href = '/admin';
        return;
      }
    } catch (err) {
      // Ignore admin login error, try user login next
    }
    
    try {
      const loginData = { ...form, phone: `+967${form.phone}` };
      const res = await axios.post(`${config.API_BASE_URL}/Auth/Login`, loginData);
      
      if (res.data && res.data.success) {
        setMessage(res.data.message || t('login.loginSuccess') || 'Login successful!');
        
        if (res.data.token) {
          localStorage.setItem('token', res.data.token);
        }
        
        if (res.data.user) {
          const userObj = {
            Id: res.data.user.Id || res.data.user.id,
            FullName: res.data.user.FullName || res.data.user.fullName,
            Email: res.data.user.Email || res.data.user.email,
            Phone: res.data.user.Phone || res.data.user.phone || '',
            City: res.data.user.City || res.data.user.city || '',
            Address: res.data.user.Address || res.data.user.address || '',
            Role: res.data.user.Role || res.data.user.role || 'User',
            IsPhoneVerified: res.data.user.IsPhoneVerified || false
          };
          
          localStorage.setItem('user', JSON.stringify(userObj));
          window.dispatchEvent(new Event('userChanged'));
          
          try {
            await syncCurrencyAfterAuth(userObj.Id);
          } catch (error) {
            console.error('⚠️ Could not sync currency after login:', error);
          }
        }
        
        setTimeout(() => setRedirect(true), 1200);
      } else {
        setMessage(res.data.errorMessage || res.data.message || t('login.loginFailed'));
      }
    } catch (err) {
      if (err.response && err.response.data && (err.response.data.errorMessage || err.response.data.message)) {
        setMessage(err.response.data.errorMessage || err.response.data.message);
      } else if (err.response && err.response.data) {
        setMessage(JSON.stringify(err.response.data));
      } else {
        setMessage(t('login.loginFailed'));
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // FORGOT PASSWORD HANDLERS
  // ═══════════════════════════════════════════════════════════════
  const handleOpenForgotDialog = () => {
    setForgotPhone(form.phone || '');
    setForgotStep(1);
    setForgotCode('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotError('');
    setForgotSuccess('');
    setShowForgotNewPassword(false);
    setShowForgotConfirmPassword(false);
    setForgotDialogOpen(true);
  };

  const handleCloseForgotDialog = () => {
    if (!forgotLoading) {
      setForgotDialogOpen(false);
    }
  };

  const handleSendResetCode = async () => {
    if (!forgotPhone || forgotPhone.length !== 9) {
      setForgotError(t('register.invalidPhone', 'Please enter a valid 9-digit phone number.'));
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const fullPhone = `+967${forgotPhone}`;
      await axios.post(`${config.API_BASE_URL}/Verification/send`, { PhoneNumber: fullPhone });
      setForgotStep(2);
    } catch (err) {
      setForgotError(err?.response?.data?.message || err?.message || 'Failed to send code.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyResetCode = async () => {
    if (!forgotCode) {
      setForgotError(t('common.requiredField', 'Please enter the verification code.'));
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const fullPhone = `+967${forgotPhone}`;
      await axios.post(`${config.API_BASE_URL}/Verification/verify`, {
        PhoneNumber: fullPhone,
        Code: forgotCode
      });
      setForgotStep(3);
    } catch (err) {
      setForgotError(err?.response?.data?.message || err?.message || 'Invalid code.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPasswordSubmit = async () => {
    if (!forgotNewPassword || !forgotConfirmPassword) {
      setForgotError(t('userProfile.fillAllFields', 'Please fill in all fields.'));
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError(t('userProfile.passwordsMismatch', 'Passwords do not match.'));
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const phoneWithCode = `+967${forgotPhone}`;
      const res = await axios.post(`${config.API_BASE_URL}/UsersApi/reset-password`, {
        Phone: phoneWithCode,
        NewPassword: forgotNewPassword,
        ConfirmPassword: forgotConfirmPassword
      });
      if (res.data && res.data.success) {
        setForgotSuccess(t('userProfile.passwordChanged', 'Password reset successfully!'));
        setTimeout(() => {
          setForgotDialogOpen(false);
          setForgotSuccess('');
        }, 2000);
      } else {
        setForgotError(res.data.message || 'Failed to reset password.');
      }
    } catch (err) {
      setForgotError(err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message || 'An error occurred.');
    } finally {
      setForgotLoading(false);
    }
  };

  const forgotStepLabels = [
    t('login.phone', 'Phone'),
    t('login.verificationCode', 'Verify'),
    t('userProfile.newPassword', 'New Password')
  ];
  
  if (redirect) {
    window.location.href = '/select-branch';
    return null;
  }
  
  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      direction: i18n.dir(),
      py: { xs: 2, sm: 0 }
    }}>
      <Card sx={{ 
        maxWidth: { xs: '100%', sm: 400 }, 
        width: '100%', 
        p: { xs: 2, sm: 3 },
        borderRadius: { xs: '16px', sm: '20px' },
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: { xs: 1.5, sm: 2 }
      }}>
        <Avatar sx={{ 
          bgcolor: 'primary.main', 
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 }, 
          mb: { xs: 1, sm: 1.5 } 
        }}>
          <LockOpenIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />
        </Avatar>
        
        <Typography variant="h5" sx={{ 
          fontWeight: 'bold', 
          mb: 0.5, 
          color: 'primary.main',
          fontSize: { xs: '1.1rem', sm: '1.5rem' }
        }}>
          {t('login.loginButton')}
        </Typography>
        
        <Typography variant="body2" sx={{ 
          color: 'text.secondary', 
          mb: { xs: 1, sm: 1.5 },
          fontSize: { xs: '0.8rem', sm: '0.875rem' }
        }}>
          {t('login.signInToContinue')}
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: { xs: 1.5, sm: 2 } 
        }}>
          <TextField 
            name="phone" 
            label={t('login.phone') || "Phone Number"} 
            variant="outlined" 
            value={form.phone} 
            onChange={handleChange} 
            required 
            fullWidth 
            size={isMobile ? "small" : "medium"}
            sx={{ 
              '& .MuiOutlinedInput-root': { borderRadius: { xs: '10px', sm: '12px' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '1rem' } }
            }} 
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ direction: 'ltr' }}>
                  <Typography sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, fontWeight: 500 }}>
                    +967
                  </Typography>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
          />
          
          <TextField
            name="password"
            label={t('login.password')}
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            value={form.password}
            onChange={handleChange}
            required
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{ 
              '& .MuiOutlinedInput-root': { borderRadius: { xs: '10px', sm: '12px' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '1rem' } }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(v => !v)} edge="end" size={isMobile ? "small" : "medium"}>
                    {showPassword ? <VisibilityOff sx={{ fontSize: { xs: 18, sm: 24 } }} /> : <Visibility sx={{ fontSize: { xs: 18, sm: 24 } }} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            InputLabelProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
          />

          {/* ✅ Forgot Password Link */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.5 }}>
            <Button
              variant="text"
              size="small"
              onClick={handleOpenForgotDialog}
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                textTransform: 'none',
                p: 0,
                minWidth: 'auto',
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
              }}
            >
              {t('login.forgotPassword', 'Forgot Password?')}
            </Button>
          </Box>
          
          <Button 
            type="submit" 
            variant="contained"
            size={isMobile ? "medium" : "large"}
            sx={{ 
              mt: { xs: 0.5, sm: 1 }, 
              py: { xs: 1.2, sm: 1.5 }, 
              fontWeight: 'bold', 
              fontSize: { xs: '0.9rem', sm: '1rem' }, 
              borderRadius: { xs: '10px', sm: '12px' }, 
              bgcolor: 'primary.main', 
              color: 'primary.contrastText', 
              '&:hover': { bgcolor: 'primary.dark' },
              '&:active': { transform: 'scale(0.98)' },
              transition: 'all 0.2s'
            }}
          >
            {t('login.loginButton')}
          </Button>
        </Box>
        
        {message && (
          <Typography variant="body2" sx={{ 
            mt: 1, 
            color: message.includes('success') || message.includes('بنجاح') ? 'success.main' : 'error.main',
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            textAlign: 'center'
          }}>
            {message}
          </Typography>
        )}

        {/* Sign up prompt */}
        <Box sx={{ mt: { xs: 2, sm: 3 }, width: '100%', textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
            {t('login.noAccount')}
          </Typography>
          <Button
            variant="contained"
            size={isMobile ? "medium" : "large"}
            sx={{ 
              mt: 1, fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' }, 
              borderRadius: { xs: '10px', sm: '12px' }, bgcolor: 'primary.main', color: 'primary.contrastText', 
              '&:hover': { bgcolor: 'primary.dark' }, '&:active': { transform: 'scale(0.98)' }, transition: 'all 0.2s'
            }}
            fullWidth
            onClick={() => setRegisterOpen(true)}
          >
            {t('login.signUp')}
          </Button>
        </Box>
        
        <RegisterDrawer open={registerOpen} onClose={() => setRegisterOpen(false)} />
      </Card>

      {/* FORGOT PASSWORD DIALOG */}
      <Dialog
        open={forgotDialogOpen}
        onClose={handleCloseForgotDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '24px', p: { xs: 1, sm: 2 } } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          <Avatar sx={{ bgcolor: '#F3F4F6', width: 48, height: 48 }}>
            <KeyIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
          </Avatar>
          {t('login.forgotPassword', 'Forgot Password')}
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Stepper activeStep={forgotStep - 1} alternativeLabel sx={{ mb: 1 }}>
            {forgotStepLabels.map((label, index) => (
              <Step key={index} completed={forgotStep > index + 1}>
                <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: { xs: '0.7rem', sm: '0.8rem' } } }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {forgotSuccess && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: '12px' }}>
              {forgotSuccess}
            </Alert>
          )}

          {forgotError && (
            <Alert severity="error" sx={{ borderRadius: '12px' }}>{forgotError}</Alert>
          )}

          {forgotStep === 1 && !forgotSuccess && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {t('login.resetDesc', 'Enter your phone number to receive a WhatsApp verification code.')}
              </Typography>
              <TextField
                label={t('login.phone', 'Phone Number')}
                value={forgotPhone}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
                  setForgotPhone(val);
                }}
                fullWidth
                size="small"
                placeholder="7XXXXXXXX"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ direction: 'ltr' }}>
                      <WhatsAppIcon sx={{ color: '#25D366', fontSize: 20, mr: 0.5 }} />
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>+967</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#F9FAFB' } }}
              />
            </>
          )}

          {forgotStep === 2 && !forgotSuccess && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {t('login.enterCodeDesc', 'Enter the 6-digit verification code sent to your WhatsApp.')}
              </Typography>
              <TextField
                label={t('login.verificationCode', 'Verification Code')}
                value={forgotCode}
                onChange={e => setForgotCode(e.target.value)}
                fullWidth
                size="small"
                placeholder="123456"
                inputProps={{ maxLength: 6 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SmsIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#F9FAFB' } }}
              />
            </>
          )}

          {forgotStep === 3 && !forgotSuccess && (
            <>
              <Box sx={{ bgcolor: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '14px', p: 2 }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#0369A1', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  🔒 {t('userProfile.passwordRequirements', 'Password Requirements:')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  {[
                    t('userProfile.passwordReqLength', 'At least 8 characters'),
                    t('userProfile.passwordReqUppercase', 'At least one uppercase letter (A-Z)'),
                    t('userProfile.passwordReqLowercase', 'At least one lowercase letter (a-z)'),
                    t('userProfile.passwordReqNumber', 'At least one number (0-9)'),
                    t('userProfile.passwordReqSpecial', 'At least one special character (!@#$%^&*)'),
                  ].map((req, i) => (
                    <Box component="li" key={i} sx={{ color: '#0C4A6E', fontSize: '0.78rem', lineHeight: 1.5 }}>{req}</Box>
                  ))}
                </Box>
              </Box>

              <TextField
                label={t('userProfile.newPassword', 'New Password')}
                type={showForgotNewPassword ? 'text' : 'password'}
                value={forgotNewPassword}
                onChange={e => setForgotNewPassword(e.target.value)}
                fullWidth
                size="small"
                placeholder="••••••••"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockResetIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowForgotNewPassword(v => !v)} edge="end" size="small">
                        {showForgotNewPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#F9FAFB' } }}
              />

              <TextField
                label={t('userProfile.confirmPassword', 'Confirm New Password')}
                type={showForgotConfirmPassword ? 'text' : 'password'}
                value={forgotConfirmPassword}
                onChange={e => setForgotConfirmPassword(e.target.value)}
                fullWidth
                size="small"
                placeholder="••••••••"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CheckCircleIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowForgotConfirmPassword(v => !v)} edge="end" size="small">
                        {showForgotConfirmPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#F9FAFB' } }}
              />
            </>
          )}
        </DialogContent>

        {!forgotSuccess && (
          <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
            <Button
              onClick={handleCloseForgotDialog}
              disabled={forgotLoading}
              sx={{ flex: 1, borderRadius: '12px', py: 1.5, fontWeight: 'bold', bgcolor: '#F3F4F6', color: '#374151', '&:hover': { bgcolor: '#E5E7EB' } }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={
                forgotStep === 1 ? handleSendResetCode :
                forgotStep === 2 ? handleVerifyResetCode :
                handleResetPasswordSubmit
              }
              disabled={forgotLoading}
              variant="contained"
              sx={{ flex: 1, borderRadius: '12px', py: 1.5, fontWeight: 'bold' }}
            >
              {forgotLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                forgotStep === 1 ? t('common.sendCode', 'Send Code') :
                forgotStep === 2 ? t('common.verifyCode', 'Verify') :
                t('common.save', 'Save')
              )}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default Login;
